<template>
  <!-- No page box / title strip: the navbar breadcrumb names the page
       (see specs/header.md). Two detached rounded cards on the grey page
       background: the actions card and the table card. -->
  <div class="saleOrders-vue-wrapper m-2 md:m-4 md:mt-2.5 md:mr-5 w-auto flex flex-col gap-2 md:gap-3">
    <!-- gap-2 on phones (search wraps to its own row, rows stay tight);
         wider breathing room between pills / search / sync from sm up. -->
    <div class="saleOrders-header vp-card flex items-center flex-wrap gap-2 sm:gap-4 p-2.5 md:p-3">
      <!-- One-tap status filter (server-side fetch per status). -->
      <div class="status-pills flex items-center gap-0.5 bg-white border border-gray-300 rounded-full p-0.5">
        <button
          v-for="option in pricingStatuses"
          :key="option.value"
          type="button"
          class="status-pill"
          :class="{ 'status-pill--active': pricingStatus === option.value }"
          @click="pricingStatus = option.value"
        >
          {{ option.name }}
        </button>
      </div>
      <!-- Quick search: client-side filter across the loaded rows. Inline
           between the pills and the sync button from `sm` up; on phones the
           `search-field` order/width drop it to its own row at the bottom. -->
      <div class="search-field relative">
        <i
          class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"
        ></i>
        <input
          v-model="searchText"
          type="text"
          :placeholder="searchPlaceholder"
          class="input input-sm input-bordered w-full pl-8 pr-8 text-sm font-normal focus:outline-none bg-white border-gray-300"
        />
        <button
          v-if="searchText"
          type="button"
          aria-label="Clear search"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          @click="searchText = ''"
        >
          <i class="fa-solid fa-circle-xmark"></i>
        </button>
      </div>
      <button
        :disabled="isRFQLoading"
        @click="onLoadRFQ"
        title="Load Latest RFQs"
        :class="[
          'load-rfq-button btn btn-neutral btn-xs text-white disabled:text-neutral-content h-7 px-2 rounded-md normal-case',
          { loading: isRFQLoading },
        ]"
      >
        <span v-if="!isRFQLoading" class="mr-2">
          <i class="fa-solid fa-download"></i>
        </span>
        <!-- Phones: drop only the word "Load" so the row fits while the
             button stays recognizably the same. -->
        <span class="font-semibold hidden sm:inline">Load Latest RFQs</span>
        <span class="font-semibold sm:hidden">Latest RFQs</span>
      </button>
    </div>
    <!-- No card around the table: the grid itself is the outermost box and
         carries the vp-card radius / border color (scoped rule below), so
         there is no double frame around the rows. flex-1 makes the table
         area absorb all viewport height left under the actions card (the
         wrapper is full-height, see scoped CSS), so the grid's bottom
         border hugs the bottom of the screen instead of a fixed 71vh. -->
    <div class="tabContainer tablesWrapper flex-1 min-h-0">
      <!-- =============== Feedback Message (loading / error) =============== -->
      <div
        v-if="isFeedbackMessageComputed"
        class="h-full p-5 py-10 bg-white border border-gray-200 rounded-xl ag-theme-alpine"
      >
        <div
          v-if="saleOrdersDataComputed.isLoading"
          class="flex flex-row text-center text-primary justify-center items-center"
        >
          <i class="fa-solid fa-arrows-rotate"></i>
          <p class="ml-2 mb-[0.5px] font-semibold">Searching for Pending Sale Orders...</p>
        </div>
        <div
          class="flex flex-row text-center justify-center items-center text-red-500"
          v-else-if="saleOrdersDataComputed.error.length"
        >
          <i class="fa-solid fa-circle-exclamation"></i>
          <p class="ml-2 mb-[0.5px] font-semibold">
            {{ saleOrdersDataComputed.error }}
          </p>
        </div>
      </div>
      <!-- =============== SaleOrders Data =============== -->
      <!-- Rendered even when the dataset is empty (columnsDefs are static),
           so every empty state — no data OR no search matches — shows the
           same headers with a message in the same overlay position. -->
      <div v-if="!isFeedbackMessageComputed && saleOrdersDataComputed.data.columnsDefs" class="h-full">
        <!-- Height MUST be passed as style, not a class: DataGrid declares
             `class` as a prop and forwards it to the inner grid element, so
             a height class never reaches the root wrapper — the grid then
             paints once and collapses to zero. `style` falls through to the
             root (same mechanism the old fixed 71vh used). -->
        <DataGrid
          :columnsDefs="columnsDefsComputed"
          :rowsData="saleOrdersDataComputed.data.rowsData"
          :rowClass="['cursor-pointer']"
          :quickFilterText="debouncedSearchText"
          @rowClicked="onClickRow"
          class="ag-theme-alpine"
          style="height: 100%"
        >
          <template #empty>
            <!-- Empty RFQ queue means nothing needs pricing — good news, so
                 it gets a positive message; same grey icon + typography as
                 the other empty states for consistency. -->
            <template v-if="!hasRowsComputed && pricingStatus === 'RFQ'">
              <i class="mdi mdi-check-circle-outline text-4xl mb-2"></i>
              <span class="font-medium">All caught up!</span>
              <span class="text-sm mt-1">No RFQs waiting to be priced.</span>
            </template>
            <template v-else-if="!hasRowsComputed">
              <i class="mdi mdi-file-search-outline text-4xl mb-2"></i>
              <span class="font-medium">No sale orders found</span>
              <span class="text-sm mt-1">Try another status or Load Latest RFQs.</span>
            </template>
            <template v-else>
              <i class="mdi mdi-file-search-outline text-4xl mb-2"></i>
              <span class="font-medium">No results found</span>
              <span class="text-sm mt-1">Try adjusting your search criteria</span>
            </template>
          </template>
        </DataGrid>
      </div>
    </div>
  </div>
</template>

<script>
import DataGrid from '@/components/DataGrid/DataGrid.vue';
import { useStore } from 'vuex';
import { ref, watch, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import * as databaseSyncServices from '../services/databaseSync.service';
import { dispatchToast } from '@/utils/dispatchToast';
import EventTracker from '@/utils/EventTracker';
import { needsRefresh } from '@/utils/helpers';
import { useCompactTables } from '@/composables/useCompactTables';

export default {
  name: 'SaleOrdersView',
  components: {
    DataGrid,
  },
  setup() {
    const pricingStatuses = [
      { name: 'RFQ', value: 'RFQ' },
      { name: 'Posted', value: 'POSTED' },
      { name: 'All', value: 'All' },
    ];
    const pricingStatus = ref(pricingStatuses[0].value);

    // Free-text quick search over the loaded rows (AG Grid quick filter:
    // matches any visible column — order name, customer, date, CSM, status).
    // The grid receives a debounced copy so it filters once per pause in
    // typing, not on every keystroke; clearing applies immediately.
    const searchText = ref('');
    const debouncedSearchText = ref('');
    let searchDebounceTimer;
    watch(searchText, (value) => {
      clearTimeout(searchDebounceTimer);
      if (!value) {
        debouncedSearchText.value = '';
        return;
      }
      searchDebounceTimer = setTimeout(() => {
        debouncedSearchText.value = value;
      }, 250);
    });

    const store = useStore();
    const router = useRouter();

    const isRFQLoading = ref(false);

    // ========================= Lifecycle hooks =========================
    onMounted(() => {
      EventTracker.rfqView();
      store.dispatch('saleOrders/saleOrders', pricingStatus.value);
    });

    // ========================= Watchers =========================
    watch(pricingStatus, () => {
      store.dispatch('saleOrders/saleOrders', pricingStatus.value);
    });

    const saleOrdersDataComputed = computed(() => store.getters['saleOrders/saleOrders']);

    // On phones held upright only the columns needed to find and open an SO
    // are shown, with tighter minimum widths so they fit without horizontal
    // scrolling. Tilting to landscape brings the full column set back (the
    // grid then scrolls horizontally if needed) — see specs/mobile.md.
    // Headers shrink along with the widths: four columns on a 393px screen
    // only fit if the words do too. Health keeps no header at all — the column
    // is one arrow wide there, and a word over it would be the widest thing in
    // it; the sort arrow still appears when the header is tapped.
    const compactTables = useCompactTables();
    // The grid on a 393px iPhone has 362px to give once the page margins and
    // its own vertical scrollbar are out. Go over it and AG Grid scrolls
    // horizontally, and the column pushed off the right edge stops rendering
    // its cells at all, so every pixel here is spoken for. Measured, not
    // guessed: an order name is 56px of text, "Customer" is 71px of header
    // text, and "Posted" with the price list icon beside it is 79px.
    //
    // Two things pay for the health column showing its number. `suppressMenu`
    // drops AG Grid's filter button, which costs 16px of every header (the
    // difference between "Customer" and "Cust..."), and the scoped rule below
    // trims AG Grid's 17/18px of horizontal padding to 10px. The column filters
    // are still there on a desktop and on a landscape phone; the quick search
    // above the grid is the filter that fits a phone anyway.
    const mobileColumns = {
      // An order name is 56.1-56.4px of text, and the cell spends 22px on
      // padding and AG Grid's own wrapper rather than the 20px the padding
      // alone suggests. At 78 the text ran a few tenths of a pixel past its
      // box, which was enough for the ellipsis to eat three characters: five of
      // six rows read "S223...". Everything spare past 80 goes to Customer,
      // which is the one column here whose content has no natural length.
      orderName: { minWidth: 80, headerName: 'Order', suppressMenu: true },
      customer: { minWidth: 113, suppressMenu: true },
      pricingStatus: { minWidth: 99, headerName: 'Status', suppressMenu: true },
      // Pinned rather than flexed, and sized for its worst case rather than the
      // common "2.1%": with no arrow on a phone that is "179.5%" at 55.1px, or
      // the level reading "= 0.0%" at 54px, plus 8px of padding and the 2px the
      // cell wrapper keeps. A column that reflows when the list is sorted is
      // worse than one a few pixels wider than it usually needs. Pinning it
      // means the 14px the arrow gave up land on Customer rather than being
      // shared back out three ways.
      health: { minWidth: 70, maxWidth: 70, headerName: '', suppressMenu: true },
    };
    // AG Grid's quick filter only looks at the columns on screen, so the
    // placeholder has to name the columns that are actually there: on a phone
    // CSM is not one of them, and promising it returns an empty list for a
    // name that matches perfectly on a desktop.
    const searchPlaceholder = computed(() =>
      compactTables.value ? 'Search order, customer...' : 'Search order, customer, CSM...',
    );
    const columnsDefsComputed = computed(() => {
      const columnsDefs = saleOrdersDataComputed.value?.data?.columnsDefs || [];
      if (!compactTables.value) return columnsDefs;
      return columnsDefs
        .filter((columnDef) => columnDef.field in mobileColumns)
        .map((columnDef) => ({ ...columnDef, ...mobileColumns[columnDef.field] }));
    });

    const isFeedbackMessageComputed = computed(
      () => saleOrdersDataComputed.value.isLoading || saleOrdersDataComputed.value.error.length,
    );

    // Empty dataset (vs. "rows exist but the quick search matched nothing")
    // decides which message the grid's empty overlay shows.
    const hasRowsComputed = computed(() => Boolean(saleOrdersDataComputed.value.data.rowsData?.length));

    // ========================= Methods =========================
    const onClickRow = (event) => {
      const clickedRow = event.data;
      EventTracker.rfqClick();

      router.push({
        name: 'sale-order',
        params: { saleOrderId: clickedRow.id },
        query: { refresh: needsRefresh(clickedRow.pricingStatus), name: clickedRow.orderName },
      });
    };

    const onLoadRFQ = async () => {
      isRFQLoading.value = true;

      const { success, data } = await databaseSyncServices.databaseSyncHourly();

      if (!success) {
        dispatchToast(data.message, {
          type: 'error',
          timeout: false,
          draggable: false,
          closeOnClick: false,
        });
      } else {
        dispatchToast('Records Updated Successfully!', { type: 'success' });
      }

      EventTracker.rfqLoad(success);
      store.dispatch('saleOrders/saleOrders', pricingStatus.value);
      isRFQLoading.value = false;
    };

    return {
      isRFQLoading,
      pricingStatus,
      pricingStatuses,
      searchText,
      searchPlaceholder,
      debouncedSearchText,
      isFeedbackMessageComputed,
      hasRowsComputed,
      saleOrdersDataComputed,
      columnsDefsComputed,
      onLoadRFQ,
      onClickRow,
    };
  },
};
</script>

<style lang="scss" scoped>
// Full viewport height: the wrapper spans the content area minus its own
// vertical margins (m-2 on phones; mt-2.5 + mb-4 from md up), so the
// flex-1 table area below the actions card ends at the bottom of the
// screen at every window size — no fixed vh.
.saleOrders-vue-wrapper {
  height: calc(100% - 16px);
}

@media (min-width: 768px) {
  .saleOrders-vue-wrapper {
    height: calc(100% - 26px);
  }
}

// The line-items grid is the outermost box of the table area (no wrapping
// card): its own outer border takes the vp-card radius and border color so
// it visually matches the actions card above without a double frame.
.tabContainer :deep(.ag-root-wrapper) {
  border-radius: 12px;
  border-color: #e5e7eb; // Tailwind gray-200, same as .vp-card
  overflow: hidden; // clip the square header background at the corners
}

// Header row matches the actions card fill (the global theme's gray-200
// header reads darker than the vp-card surface).
.tabContainer :deep(.ag-theme-alpine) {
  --ag-header-background-color: var(--vp-card-bg);
}

// Phones in portrait: AG Grid's 17/18px of horizontal padding is a fifth of a
// 90px column. Trimming it to 10px buys back ~56px across the four columns,
// which is what gets four of them onto a 393px screen at all. Matches
// `useCompactTables`, which decides the column set above.
@media (max-width: 767.98px) and (orientation: portrait) {
  .tabContainer :deep(.ag-cell),
  .tabContainer :deep(.ag-header-cell) {
    padding-left: 10px;
    padding-right: 10px;
  }

  // Health gives up more than the rest: a percentage cannot be truncated into
  // something still readable the way a customer name can. These rules have to
  // live here rather than beside the component they style, because the :deep()
  // selector above would otherwise outrank them.
  .tabContainer :deep(.price-health-cell),
  .tabContainer :deep(.price-health-header) {
    padding-left: 4px;
    padding-right: 4px;
  }

  // Right-aligned on a phone only. It is the last column and the only one
  // holding a quantity, so pushing it to the edge puts the figures against the
  // margin the eye already follows down the row, and the decimal points line
  // up under each other. On a desktop the column has room to spare and sits
  // left like every other, which is where the arrow reads best.
  .tabContainer :deep(.price-health-cell) {
    text-align: right;
  }
}

// Phones: full-width row at the bottom of the wrapping header, regardless of
// where it sits in the markup. From `sm` up it flows inline between the status
// pills and the sync button at a fixed 18rem.
.search-field {
  order: 99;
  width: 100%;
}

@media (min-width: 640px) {
  .search-field {
    order: 0;
    width: 18rem;
  }
}

// Phones only: pills stay left, the sync button is pushed to the right edge
// of the card, and the pills get tighter padding so the row fits 320px
// screens. Desktop keeps the plain left-aligned flow (pills · search · sync).
@media (max-width: 639.98px) {
  .status-pill {
    padding: 3px 10px;
  }

  .load-rfq-button {
    margin-left: auto;
  }
}

// Segmented status filter, matching the nav's navy-active pill aesthetic.
.status-pill {
  padding: 3px 14px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  color: #052a42;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }
}

.status-pill--active {
  background: #052a42;
  color: white;

  &:hover {
    background: #052a42;
  }
}
</style>
